/** @jsx jsx */
import { jsx } from "theme-ui"
import React, { useState } from "react"
import { connect } from "react-redux"
import { Link, useStaticQuery, graphql, navigate } from "gatsby"
import { Box } from "@theme-ui/components"
import styled from "styled-components"
import theme, { rem } from "@src/theme"
import Button from "@components/Button"
import Logo from "@components/assets/svg/IDAHOBIT-logo.svg"
import CarrotDown from "@components/assets/svg/carrot-down.svg"
import Bag from "@components/assets/svg/bag.svg"
import FullBag from "@components/assets/svg/full-bag.svg"
import CartModal from "@shop/components/CartModal"
import Hamburger from "@components/Hamburger"
import { Themed } from "@theme-ui/mdx"

const DropdownNavItem = ({ children, links }) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleKeyDown = e => {
    if (e.key === "Enter" || e.keyCode === 13) {
      if (isOpen) {
        setIsOpen(false)
      }
      setIsOpen(true)
    }
  }
  return (
    <Dropdown
      tabIndex="0"
      onKeyDown={handleKeyDown}
      onBlur={() => setIsOpen(false)}
      sx={{
        outline: "none",
        display: ["none", "none", null, "flex"],
        whiteSpace: "nowrap",
        "&:hover, &:active ": {
          color: "purple",
          svg: {
            color: "currentColor",
            transform: "translate(8px, -4px) rotate(180deg)",
            transition: "transform 250ms ease",
          },
        },
      }}
    >
      <Themed.h6
        sx={{
          my: 0,
          pr: [rem(18), null, null, null, rem(40)],
          alignItems: "center",
          cursor: "pointer",
          fontSize: `${rem(18)}!important`,
        }}
      >
        <span> {children}</span>
        <CarrotDown
          sx={{
            transform: "translate(5px, -1px)",
            color: "currentColor",
            fill: "currentColor",
          }}
        />
      </Themed.h6>

      <div
        className={`dropdown-content ${isOpen ? `is-open` : ``}`}
        sx={{
          pt: 4,
          left: "-1.33rem",
          top: rem(17),
        }}
      >
        {links.map(({ label, link }) => (
          <Link to={link} key={link}>
            <Themed.h6
              tabIndex={isOpen || "0"}
              to={link}
              sx={{
                pb: 3,
                px: 3,
                m: 0,
                textDecoration: "underline",
                color: "darkPurple",
                lineHeight: rem(24),
                whiteSpace: "nowrap",
                fontSize: `${rem(18)}!important`,
                width: "100%",
                "&:hover": {
                  color: theme.colors.purple,
                },
              }}
              activestyle={{ color: theme.colors.purple }}
            >
              {label}
            </Themed.h6>
          </Link>
        ))}
      </div>
    </Dropdown>
  )
}

const NavLabel = ({ link, label, onClick }) => (
  <Themed.h6
    as="li"
    sx={{
      display: ["none", "none", "none", "block"],
      fontSize: `${rem(18)}!important`,
      pr: [rem(18), null, null, null, rem(30)],
      "&:hover": {
        color: "purple",
        cursor: "pointer",
      },
    }}
    onClick={onClick}
  >
    <Link
      to={link}
      className="unstyled-link"
      activeStyle={{ color: theme.colors.purple }}
    >
      {label}
    </Link>
  </Themed.h6>
)

const Dropdown = styled.li`
  position: relative;

  .dropdown-content {
    display: none;
    position: absolute;
    background-color: #fff;
    z-index: 1;
  }
  .dropdown-content a {
    text-align: left;
  }

  &:hover .dropdown-content,
  .dropdown-content.is-open {
    display: flex;
    flex-direction: column;
    align-items: baseline;
  }
`

const Nav = ({
  closeCartModal,
  openCartModal,
  cartModalOpen,
  checkout,
  openDonationModal,
  navOpen,
  toggleNav,
}) => {
  const data = useStaticQuery(
    graphql`
      query {
        pageData: markdownRemark(
          fileAbsolutePath: { glob: "**/globals/IDAHOBIT.md" }
        ) {
          frontmatter {
            idahobitDay
          }
        }
      }
    `
  )

  const { idahobitDay } = data.pageData.frontmatter

  return (
    <nav
      sx={{
        position: "relative",
        zIndex: 9,
      }}
      id="nav1"
    >
      <div
        className="p-left"
        sx={{
          px: [rem(12), null, "2.5vw"],
        }}
      >
        <Box
          sx={{
            listStyleType: "none",
            display: "flex",
            justifyContent: "space-between",
          }}
          columns={[3]}
        >
          <Box
            sx={{
              display: ["block", "block", "block", "none"],
              alignSelf: "center",
              width: "90px",
            }}
          >
            <Hamburger />
          </Box>
          <Box
            sx={{
              display: ["none", "none", "none", "flex"],
              alignItems: "center",
              flex: [0, 0, 0, 1],
            }}
          >
            <DropdownNavItem
              links={[
                { label: "May 17", link: "/about-may-17" },
                { label: "Our Impact", link: "/our-impact" },
              ]}
            >
              What is IDAHOBIT
            </DropdownNavItem>
            <DropdownNavItem
              links={[
                { label: "Identity and Stats", link: "/identity-and-stats" },
                { label: "People and Stories", link: "/people-and-stories" },
                { label: "Allyship", link: "/allyship" },
              ]}
            >
              Explore Inclusion
            </DropdownNavItem>
            <DropdownNavItem
              links={[
                { label: "Get Started", link: "/get-started" },
                { label: "Host an Activity", link: "/host-an-activity" },
                {
                  label: "Training and Speakers",
                  link: "/training-and-speakers",
                },
                { label: "Resources", link: "/resources" },
                { label: "Fundraise", link: "/fundraise" },
                { label: "Partner", link: "/partner" },
              ]}
            >
              Get Involved
            </DropdownNavItem>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: [0, 0, 0, 2],
            }}
          >
            <Link
              to="/"
              className="unstyled-link"
              sx={{
                transform: "translateY(6px)",
              }}
            >
              <Logo
                sx={{
                  width: [rem(95), null, rem(190)],
                  height: [rem(44), rem(52)],
                }}
              />
            </Link>
          </Box>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              minWidth: "90px !important",
              justifyContent: "flex-end",
              flex: [0, 0, 0, 1],
            }}
          >
            <NavLabel label="Shop" link="/shop" />
            {idahobitDay ? (
              <>
                <NavLabel label="Register" link="/register" />
                <Box
                  as="li"
                  tabIndex="0"
                  sx={{
                    pr: [rem(18), null, null, null, rem(40)],
                    display: ["none", null, "block", null],
                    position: "relative",
                    zIndex: 888,
                  }}
                >
                  <Button onClick={() => openDonationModal()}>Donate</Button>
                </Box>
              </>
            ) : (
              <>
                <NavLabel onClick={openDonationModal} label="Donate" />
                <Box
                  as="li"
                  tabIndex="0"
                  sx={{
                    pr: [rem(18), null, null, null, rem(40)],
                    display: ["none", null, "block", null],
                    position: "relative",
                    zIndex: 888,
                  }}
                >
                  <Button onClick={() => navigate("/register")}>
                    Register
                  </Button>
                </Box>
              </>
            )}

            <Box
              as="li"
              sx={{
                transform: "translateY(3px)",
                display: "flex",
              }}
              onClick={() => {
                cartModalOpen ? closeCartModal() : openCartModal()
              }}
            >
              <button
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => {
                  if (navOpen) {
                    toggleNav()
                  }
                  return cartModalOpen ? closeCartModal() : openCartModal()
                }}
              >
                {checkout && (checkout.lines?.edges?.length || checkout.lineItems?.length) ? (
                  <div sx={{ position: "relative" }}>
                    <FullBag
                      sx={{
                        width: [rem(19), null, "initial"],
                      }}
                    />
                    <div
                      sx={{
                        position: "absolute",
                        bg: "darkPurple",
                        color: "white",
                        minWidth: "16px",
                        height: "16px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                        top: "1px",
                        right: "-6px",
                        fontSize: "10px",
                      }}
                    >
                      {checkout.lines?.edges?.length || checkout.lineItems?.length || 0}
                    </div>
                  </div>
                ) : (
                  <Bag
                    sx={{
                      width: [rem(19), null, "initial"],
                      "&:hover": {
                        color: "purple",
                      },
                    }}
                  />
                )}
              </button>
            </Box>
          </Box>
        </Box>
      </div>

      {cartModalOpen && (
        <CartModalOuter
          sx={{
            position: "absolute",
            bg: "white",
            right: 0,
          }}
        >
          <div
            sx={{
              p: [4, null, 6],
              bg: "white",
            }}
          >
            <CartModal onClose={closeCartModal} />
          </div>
        </CartModalOuter>
      )}
    </nav>
  )
}

export default connect(
  state => ({
    cartModalOpen: state.cartModal.cartModalOpen,
    checkout: state.cart.checkout,
    navOpen: state.ui.navOpen,
  }),
  dispatch => ({
    closeCartModal: () => dispatch.cartModal.toggleCartModal(false),
    openCartModal: () => dispatch.cartModal.toggleCartModal(true),
    openDonationModal: () => dispatch.donationModal.toggleDonationModal(true),
    toggleNav: () => dispatch.ui.toggleNav(),
  })
)(Nav)

const CartModalOuter = styled.div`
  /* This way it could be display flex or grid or whatever also. */
  display: block;
  /* Probably need media queries here */
  width: 600px;
  max-width: 90%;
  background: white;
`
